import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Checkbox,
  Button,
  IconButton,
  InputBase,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableChartIcon from "@mui/icons-material/TableChart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RefreshIcon from "@mui/icons-material/Refresh";
import CommentIcon from "@mui/icons-material/AddComment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import API_BASE_URL from "../../config/api";
import {
  isTimeMeasureExportUser,
  fetchTimeMeasureExportOptions,
} from "../../utils/timeMeasureExport";
import { checkUsernameAdmin } from "./filterUtils";
import { convertToXLSX, generateFakturaReport } from "./exportUtils";
import {
  COLORS,
  EMPTY_FILTERS,
  PAGE_SIZE_OPTIONS,
  TRANSPORT_OPTIONS,
  countActiveFilters,
  describeFilters,
  getDefaultHiddenColumns,
  getMarkerColor,
  getStatusTone,
  getViewConfig,
} from "./dashboardConfig";
import {
  useDashboardData,
  createAuthHeaders,
  fetchDashboardPage,
} from "./useDashboardData";
import FiltersDrawer from "./FiltersDrawer";
import Row, { TransportLed } from "./Row";

const useStyles = makeStyles((theme) => ({
  main: {
    minHeight: "100vh",
    background: "#fff",
    transition: "margin-left 0.2s ease",
    padding: theme.spacing(3, 4, 4),
    boxSizing: "border-box",
    minWidth: 0,
  },
  pageHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
    color: COLORS.primaryDark,
    lineHeight: 1.2,
  },
  subtitle: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: 14,
    color: COLORS.textMuted,
  },
  headerActions: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  card: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },
  search: {
    display: "flex",
    alignItems: "center",
    flex: "1 1 320px",
    maxWidth: 520,
    height: 40,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: "#fafafa",
    transition: "background-color 0.2s, box-shadow 0.2s, border-color 0.2s",
    "&:hover": { background: "#f0f0f0" },
    "&:focus-within": {
      background: "#fff",
      borderColor: COLORS.primary,
      boxShadow: "0 0 0 3px rgba(25,118,210,0.15)",
    },
    "& svg": { color: "#757575" },
  },
  searchInput: { flex: 1, marginLeft: theme.spacing(1), fontSize: 14.5 },
  kbd: {
    padding: "1px 7px",
    borderRadius: 4,
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    color: "#9e9e9e",
    fontSize: 12,
    fontFamily: "inherit",
  },
  toolbarGap: { flex: 1 },
  presetChip: {
    height: 32,
    fontWeight: 600,
    fontSize: 13.5,
  },
  quickBlock: {
    padding: theme.spacing(0, 2, 1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  quickRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  quickLabel: {
    width: 74,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#9e9e9e",
  },
  facet: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 30,
    padding: "0 12px",
    borderRadius: 16,
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    color: "#424242",
    fontSize: 13.5,
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background-color 0.15s, border-color 0.15s",
    "&:hover": { background: "#f5f9ff", borderColor: "#90caf9" },
  },
  facetActive: {
    background: COLORS.primarySoft,
    borderColor: COLORS.primary,
    color: COLORS.primaryDark,
    fontWeight: 700,
  },
  facetCount: {
    fontSize: 12,
    fontWeight: 700,
    color: "#757575",
    fontVariantNumeric: "tabular-nums",
  },
  activeFilters: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 2, 1.5),
    borderTop: `1px solid ${COLORS.borderSoft}`,
  },
  selectionBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    background: COLORS.primarySoft,
    borderTop: `1px solid #bbdefb`,
    borderLeft: `5px solid ${COLORS.primary}`,
    fontSize: 14,
    color: COLORS.primaryDark,
  },
  tableWrap: {
    position: "relative",
    maxHeight: "calc(100vh - 150px)",
    minHeight: 240,
    overflow: "auto",
    borderTop: `1px solid ${COLORS.border}`,
  },
  progress: { position: "absolute", top: 0, left: 0, right: 0, zIndex: 5, height: 3 },
  headCell: {
    backgroundColor: "#f5f8fc !important",
    borderBottom: `2px solid ${COLORS.primary} !important`,
    padding: "10px 12px !important",
    fontSize: "12.5px !important",
    fontWeight: "800 !important",
    letterSpacing: 0.3,
    color: "#37474f !important",
    whiteSpace: "nowrap",
    "&.MuiTableCell-paddingCheckbox": { padding: "0 2px 0 12px !important" },
    "&.MuiTableCell-paddingNone": { padding: "0 !important" },
  },
  headSticky: { position: "sticky", zIndex: 4 },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(8, 2),
    color: COLORS.textMuted,
    textAlign: "center",
  },
  error: {
    margin: theme.spacing(1.5, 2),
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    background: "#ffebee",
    borderLeft: `5px solid ${COLORS.danger}`,
    color: "#b71c1c",
    fontSize: 14,
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75, 2),
    padding: theme.spacing(1.25, 2),
    fontSize: 13,
    color: COLORS.textMuted,
  },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  legendTone: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
}));

const SortHeader = ({ column, sortBy, sortDir, onSort, className, style }) => {
  const active = column.sortKey && sortBy === column.sortKey;
  return (
    <TableCell className={className} style={style}>
      {column.sortKey ? (
        <TableSortLabel
          active={Boolean(active)}
          direction={active ? sortDir : "asc"}
          onClick={() => onSort(column.sortKey)}
        >
          {column.label}
        </TableSortLabel>
      ) : (
        column.label
      )}
    </TableCell>
  );
};

const loadView = (storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (saved && typeof saved === "object") return saved;
  } catch (e) {
    // uszkodzony zapis - użyj wartości domyślnych
  }
  return {};
};

const mergeFilters = (patch) => ({ ...EMPTY_FILTERS, ...(patch || {}) });

// view: odprawy (dashboard) | archiwum | tranzyt | tranzytArchiwum - patrz VIEW_CONFIG.
const UserDashboard = ({ view = "odprawy" }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const config = getViewConfig(view);
  const defaultHiddenColumns = useMemo(() => getDefaultHiddenColumns(view), [view]);

  const isAdmin = useMemo(() => checkUsernameAdmin(), []);

  const token = localStorage.getItem("token");
  let username;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      username = decoded.username;
    } catch (e) {
      // nieprawidłowy token - obsłuży go inicjalizacja (wylogowanie)
    }
  }

  // ---- Widok zapamiętywany między sesjami (filtry, sortowanie, kolumny) ----
  const initialView = useRef(loadView(config.storageKey)).current;
  const [filters, setFilters] = useState(() => mergeFilters(initialView.filters));
  const [sortBy, setSortBy] = useState(initialView.sortBy || null);
  const [sortDir, setSortDir] = useState(initialView.sortDir || "asc");
  const [pageSize, setPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(initialView.pageSize) ? initialView.pageSize : 50,
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    Array.isArray(initialView.hiddenColumns)
      ? initialView.hiddenColumns
      : defaultHiddenColumns,
  );
  const [page, setPage] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(
        config.storageKey,
        JSON.stringify({ filters, sortBy, sortDir, pageSize, hiddenColumns }),
      );
    } catch (e) {
      // brak miejsca / tryb prywatny - widok po prostu nie zostanie zapamiętany
    }
  }, [config.storageKey, filters, sortBy, sortDir, pageSize, hiddenColumns]);

  // ---- Preferencje w bazie (kolumny + rozmiar strony) ----
  // syncedPrefs = ostatnio zsynchronizowana z serwerem wartość (null = serwer nie ma
  // jeszcze zapisu, wtedy pierwszy zapis wysyła dotychczasowe ustawienia lokalne).
  const syncedPrefs = useRef(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    if (!prefsLoaded) return undefined;
    const payload = { view, hiddenColumns, pageSize };
    const serialized = JSON.stringify({ hiddenColumns, pageSize });
    if (serialized === syncedPrefs.current) return undefined;
    const timeout = setTimeout(async () => {
      try {
        await axios.put(`${API_BASE_URL}/me/dashboard-preferences`, payload, {
          headers: createAuthHeaders(),
        });
        syncedPrefs.current = serialized;
      } catch (e) {
        console.error("Nie udało się zapisać preferencji dashboardu:", e);
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [prefsLoaded, view, hiddenColumns, pageSize]);

  // ---- UI ----
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);
  const searchRef = useRef(null);

  // ---- Zaznaczanie (przetrwa zmianę strony i filtrów) ----
  const [selected, setSelected] = useState({});
  const selectedRows = useMemo(() => Object.values(selected), [selected]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ---- Dane słownikowe ----
  const [options, setOptions] = useState({ lokalizacje: [], osoby: [] });

  const handleLogout = useCallback(() => {
    localStorage.clear();
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Use-New-Db"];
    toast.success("Wylogowano.", {
      autoClose: 500,
      onClose: () => navigate("/login"),
    });
  }, [navigate]);

  // Inicjalizacja sesji: sprawdzenie tokena i wybór bazy, potem listy dla filtrów.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        if (mounted) handleLogout();
        return;
      }
      try {
        const { data: me } = await axios.get(`${API_BASE_URL}/me`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!mounted) return;
        localStorage.setItem("useNewDb", me.useNewDb ? "true" : "false");

        const headers = createAuthHeaders();

        // Kolumny i rozmiar strony pochodzą z konta użytkownika (baza danych), osobno dla każdego widoku.
        // Błąd nie blokuje widoku - zostają wartości z localStorage/domyślne.
        try {
          const { data: prefs } = await axios.get(
            `${API_BASE_URL}/me/dashboard-preferences`,
            { headers, params: { view } },
          );
          if (!mounted) return;
          if (Array.isArray(prefs.hiddenColumns)) {
            setHiddenColumns(prefs.hiddenColumns);
          }
          if (PAGE_SIZE_OPTIONS.includes(prefs.pageSize)) {
            setPageSize(prefs.pageSize);
          }
          syncedPrefs.current =
            Array.isArray(prefs.hiddenColumns) &&
            PAGE_SIZE_OPTIONS.includes(prefs.pageSize)
              ? JSON.stringify({
                  hiddenColumns: prefs.hiddenColumns,
                  pageSize: prefs.pageSize,
                })
              : null;
          setPrefsLoaded(true);
        } catch (prefsError) {
          console.error("Nie udało się pobrać preferencji dashboardu:", prefsError);
        }
        setReady(true);

        const [locations, people] = await Promise.all([
          axios.get(`${API_BASE_URL}/list/lokalizacjaTransportow`, { headers }),
          axios.get(`${API_BASE_URL}/list/uzytkownicy`, { headers }),
        ]);
        if (!mounted) return;
        const names = (list) =>
          list
            .map((item) => item.name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));
        setOptions({
          lokalizacje: names(locations.data),
          osoby: names(people.data),
        });
      } catch (error) {
        console.error("Błąd inicjalizacji:", error);
        if (mounted && !ready) handleLogout();
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { rows, total, facets, loading, error, lastUpdated, refresh } =
    useDashboardData({
      enabled: ready,
      view,
      filters,
      sortBy,
      sortDir,
      page,
      pageSize,
      onUnauthorized: handleLogout,
    });

  // Co minutę odśwież liczniki "Ile w systemie".
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Po usunięciu / zawężeniu wyników strona może wypaść poza zakres.
  useEffect(() => {
    if (!loading && page > 0 && rows.length === 0 && total > 0) {
      setPage(Math.max(0, Math.ceil(total / pageSize) - 1));
    }
  }, [loading, page, rows.length, total, pageSize]);

  // Skrót "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- Filtry ----
  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }, []);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const togglePreset = (preset) => {
    if (preset.isActive(filters, username)) {
      const cleared = {};
      preset.reset.forEach((key) => {
        cleared[key] = EMPTY_FILTERS[key];
      });
      updateFilters(cleared);
    } else {
      updateFilters(preset.patch(username));
    }
  };

  const toggleListValue = (key, value) => {
    const current = filters[key];
    updateFilters({
      [key]: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  };

  const activeChips = useMemo(() => describeFilters(filters), [filters]);
  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  // ---- Sortowanie: asc -> desc -> domyślna kolejność ----
  const handleSort = (key) => {
    setPage(0);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortBy(null);
      setSortDir("asc");
    }
  };

  // ---- Kolumny ----
  const visibleColumns = useMemo(
    () => config.columns.filter((c) => !hiddenColumns.includes(c.id)),
    [config.columns, hiddenColumns],
  );
  const toggleColumn = (id) =>
    setHiddenColumns((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );

  // ---- Zaznaczanie ----
  const handleSelect = useCallback((row, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) next[row.id] = row;
      else delete next[row.id];
      return next;
    });
  }, []);

  const pageAllSelected = rows.length > 0 && rows.every((r) => selected[r.id]);
  const pageSomeSelected = rows.some((r) => selected[r.id]) && !pageAllSelected;

  const handleSelectPage = (event) => {
    const { checked } = event.target;
    setSelected((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (checked) next[row.id] = row;
        else delete next[row.id];
      });
      return next;
    });
  };

  // ---- Komentarze zbiorcze ----
  const buildRecipients = (row) =>
    [
      ...new Set([row.osobaPrzygotowujacaOdprawe, row.osobaWysylajacaOdprawe]),
    ]
      .filter((item) => typeof item === "string")
      .map((item) => item.trim())
      .filter((item) => item && item !== username);

  const handleSaveBulkComment = async () => {
    const comment = bulkText.trim();
    if (!comment) {
      toast.warning("Wpisz treść komentarza.");
      return;
    }
    if (selectedRows.length === 0) {
      toast.warning("Brak zaznaczonych rekordów do zapisu komentarza.");
      return;
    }
    try {
      setBulkSaving(true);
      const items = selectedRows.map((row) => ({
        id: row.id,
        nrRef: row.nrRef,
        rodzajTransportu: row.rodzajTransportu,
        statusRealizacji: row.statusRealizacji,
        rodzaj: row.rodzaj,
        recipients: buildRecipients(row),
      }));
      const response = await axios.post(
        `${API_BASE_URL}/comments/bulk/addWithNotification`,
        {
          items,
          comment,
          username,
          timestamp: moment().tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"),
        },
        { headers: { ...createAuthHeaders(), "Content-Type": "application/json" } },
      );
      toast.success(
        `Komentarz zapisano dla ${response?.data?.processed ?? items.length} odpraw.`,
      );
      setBulkText("");
      setSelected({});
      setBulkOpen(false);
      refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Wystąpił błąd podczas zapisu komentarzy.",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // ---- Usuwanie ----
  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    try {
      await axios.delete(`${API_BASE_URL}/delete/${target.id}`, {
        headers: { ...createAuthHeaders(), "Content-Type": "application/json" },
      });
      toast.success("Poprawnie usunięto zgłoszenie!", { autoClose: 3000 });
      setSelected((prev) => {
        const next = { ...prev };
        delete next[target.id];
        return next;
      });
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      console.error("Error deleting row:", err);
      toast.error("Wystąpił błąd podczas usuwania.", { autoClose: 3000 });
    }
  };

  // ---- Eksport ----
  const handleExportXLSX = async () => {
    setExportAnchor(null);
    setExporting(true);
    try {
      // Eksport obejmuje wszystkie odprawy spełniające filtry (nie tylko bieżącą stronę).
      const data = await fetchDashboardPage({
        view,
        filters,
        sortBy,
        sortDir,
        exportAll: true,
      });
      const allRows = data.rows || [];
      const timeMeasureOptions = isTimeMeasureExportUser(username)
        ? await fetchTimeMeasureExportOptions(allRows, createAuthHeaders())
        : {};
      const workbook = convertToXLSX(allRows, timeMeasureOptions);
      XLSX.writeFile(workbook, "data.xlsx");
      toast.success(`Wyeksportowano ${allRows.length} ${config.unitLabel}.`);
    } catch (err) {
      console.error("Błąd eksportu:", err);
      toast.error("Nie udało się wyeksportować danych.");
    } finally {
      setExporting(false);
    }
  };

  const handleFakturaReport = () => {
    setExportAnchor(null);
    generateFakturaReport(rows);
  };

  // ---- Fasety (szybkie filtry z licznikami) ----
  const buildFacetOptions = (order, counts, selectedValues) => {
    const known = new Set(order);
    const extra = Object.keys(counts).filter((k) => k && !known.has(k));
    return [...order, ...extra].filter(
      (value) => (counts[value] || 0) > 0 || selectedValues.includes(value),
    );
  };
  const transportFacets = buildFacetOptions(
    TRANSPORT_OPTIONS,
    facets.rodzajTransportu,
    filters.rodzajeTransportu,
  );
  const statusFacets = buildFacetOptions(
    config.statusOptions,
    facets.statusRealizacji,
    filters.statusy,
  );

  const hasFilters = activeChips.length > 0;

  const stickyHead = [
    { left: 0, width: 52 },
    { left: 52, width: 40 },
    { left: 92 },
  ];

  return (
    <div>
      <div>
        {/* ---------------- Nagłówek strony ---------------- */}
        <div className={classes.pageHeader}>
          <div>
            <h1 className={classes.title}>{config.title}</h1>
            <div className={classes.subtitle}>
              <span>
                <strong style={{ color: "#212121" }}>{total}</strong>{" "}
                {hasFilters ? config.filteredLabel : config.countLabel}
              </span>
              {lastUpdated && (
                <span>· odświeżono {moment(lastUpdated).format("HH:mm:ss")}</span>
              )}
              <Tooltip title="Odśwież teraz">
                <IconButton size="small" onClick={refresh} aria-label="Odśwież">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className={classes.headerActions}>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setColumnsAnchor(e.currentTarget)}
            >
              Kolumny
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              disabled={exporting}
            >
              {exporting ? "Eksportuję…" : "Eksport"}
            </Button>
          </div>
        </div>

        <div className={classes.card}>
          {/* ---------------- Toolbar ---------------- */}
          <div className={classes.toolbar}>
            <div className={classes.search}>
              <SearchIcon fontSize="small" />
              <InputBase
                inputRef={searchRef}
                className={classes.searchInput}
                placeholder={config.searchPlaceholder}
                value={filters.search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                inputProps={{ "aria-label": "Szukaj" }}
              />
              {filters.search ? (
                <IconButton
                  size="small"
                  onClick={() => updateFilters({ search: "" })}
                  aria-label="Wyczyść wyszukiwanie"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : (
                <kbd className={classes.kbd}>/</kbd>
              )}
            </div>

            <Badge badgeContent={activeCount} color="primary">
              <Button
                variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                onClick={() => setFiltersOpen(true)}
                disableElevation
              >
                Filtry
              </Button>
            </Badge>

            {config.presets.map((preset) => {
              const active = preset.isActive(filters, username);
              return (
                <Tooltip key={preset.id} title={preset.hint}>
                  <Chip
                    className={classes.presetChip}
                    label={preset.label}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() => togglePreset(preset)}
                    onDelete={active ? () => togglePreset(preset) : undefined}
                  />
                </Tooltip>
              );
            })}

            <span className={classes.toolbarGap} />
            {hasFilters && (
              <Button size="small" onClick={clearAllFilters}>
                Wyczyść filtry
              </Button>
            )}
          </div>

          {/* ---------------- Szybkie filtry z licznikami ---------------- */}
          <div className={classes.quickBlock}>
            <div className={classes.quickRow}>
              <span className={classes.quickLabel}>Transport</span>
              {transportFacets.map((value) => {
                const active = filters.rodzajeTransportu.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`${classes.facet} ${active ? classes.facetActive : ""}`}
                    onClick={() => toggleListValue("rodzajeTransportu", value)}
                    aria-pressed={active}
                  >
                    <TransportLed transport={value} size={10} />
                    {value}
                    <span className={classes.facetCount}>
                      {facets.rodzajTransportu[value] || 0}
                    </span>
                  </button>
                );
              })}
              {transportFacets.length === 0 && (
                <span style={{ color: "#9e9e9e", fontSize: 13 }}>—</span>
              )}
            </div>
            <div className={classes.quickRow}>
              <span className={classes.quickLabel}>Status</span>
              {statusFacets.map((value) => {
                const active = filters.statusy.includes(value);
                const tone = getStatusTone(value);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`${classes.facet} ${active ? classes.facetActive : ""}`}
                    onClick={() => toggleListValue("statusy", value)}
                    aria-pressed={active}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: tone.fg,
                      }}
                    />
                    {value}
                    <span className={classes.facetCount}>
                      {facets.statusRealizacji[value] || 0}
                    </span>
                  </button>
                );
              })}
              {statusFacets.length === 0 && (
                <span style={{ color: "#9e9e9e", fontSize: 13 }}>—</span>
              )}
            </div>
          </div>

          {/* ---------------- Aktywne filtry ---------------- */}
          {hasFilters && (
            <div className={classes.activeFilters}>
              {activeChips.map((chip) => (
                <Chip
                  key={chip.id}
                  size="small"
                  label={chip.label}
                  onDelete={() => updateFilters(chip.clear)}
                  style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
                />
              ))}
            </div>
          )}

          {/* ---------------- Pasek zaznaczenia ---------------- */}
          {selectedRows.length > 0 && (
            <div className={classes.selectionBar}>
              <strong>Zaznaczono: {selectedRows.length}</strong>
              <Button size="small" variant="contained" startIcon={<CommentIcon />} onClick={() => setBulkOpen(true)} disableElevation>
                Dodaj komentarz
              </Button>
              <Button size="small" onClick={() => setSelected({})}>
                Odznacz wszystko
              </Button>
            </div>
          )}

          {error && <div className={classes.error}>{error}</div>}

          {/* ---------------- Tabela ---------------- */}
          <div className={classes.tableWrap}>
            {loading && <LinearProgress className={classes.progress} />}
            <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className={`${classes.headCell} ${classes.headSticky}`}
                    style={{ left: stickyHead[0].left, width: stickyHead[0].width, minWidth: stickyHead[0].width }}
                  >
                    <Checkbox
                      size="small"
                      color="primary"
                      indeterminate={pageSomeSelected}
                      checked={pageAllSelected}
                      onChange={handleSelectPage}
                      inputProps={{ "aria-label": "Zaznacz stronę" }}
                    />
                  </TableCell>
                  <TableCell
                    padding="none"
                    className={`${classes.headCell} ${classes.headSticky}`}
                    style={{ left: stickyHead[1].left, width: stickyHead[1].width, minWidth: stickyHead[1].width }}
                  />
                  <SortHeader
                    column={{ id: "nrRef", label: "Nr ref", sortKey: "nrRef" }}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                    className={`${classes.headCell} ${classes.headSticky}`}
                    style={{ left: stickyHead[2].left, width: "1%", whiteSpace: "nowrap", boxShadow: "inset -1px 0 0 #e0e0e0" }}
                  />
                  {visibleColumns.map((column) => (
                    <SortHeader
                      key={column.id}
                      column={column}
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={handleSort}
                      className={classes.headCell}
                      style={{ minWidth: column.width, maxWidth: column.width }}
                    />
                  ))}
                  <TableCell className={classes.headCell} style={{ width: 96 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    columns={visibleColumns}
                    selected={Boolean(selected[row.id])}
                    onSelect={handleSelect}
                    isAdmin={isAdmin}
                    onDelete={setDeleteTarget}
                    tick={tick}
                    from={config.detailFrom}
                  />
                ))}
              </TableBody>
            </Table>

            {!loading && rows.length === 0 && !error && (
              <div className={classes.empty}>
                <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
                <strong style={{ fontSize: 17, color: "#424242" }}>
                  {config.emptyTitle}
                </strong>
                <span>
                  {hasFilters
                    ? "Żadna odprawa nie spełnia wybranych filtrów."
                    : config.emptyText}
                </span>
                {hasFilters && (
                  <Button variant="outlined" onClick={clearAllFilters}>
                    Wyczyść filtry
                  </Button>
                )}
              </div>
            )}
          </div>

          <TablePagination
            component="div"
            count={total}
            page={Math.min(page, Math.max(0, Math.ceil(total / pageSize) - 1))}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            labelRowsPerPage="Wierszy na stronę:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
          />
        </div>

        {/* ---------------- Legenda ---------------- */}
        <div className={classes.legend}>
          <span className={classes.legendItem}>
            <InfoOutlinedIcon fontSize="small" /> Legenda:
          </span>
          <span className={classes.legendItem}>
            <TransportLed transport="Na Kołach" size={10} /> na kołach, czeka na odprawę
          </span>
          <span className={classes.legendItem}>
            <TransportLed transport="Kurier" size={10} /> kurier
          </span>
          <span className={classes.legendItem}>
            <TransportLed transport="Kurier MU MAN zakład" size={10} /> kurier w lokalizacji MU MAN zakład
          </span>
          <span className={classes.legendItem}>
            <TransportLed transport="W drodze" size={10} /> w drodze
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendTone} style={{ background: "#fff59d", border: "1px solid #f57c00" }} /> rewizja
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendTone} style={{ background: getMarkerColor("default") }} /> braki w odprawie
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendTone} style={{ background: getMarkerColor("red") }} /> dodatkowe ograniczenia
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendTone} style={{ background: getMarkerColor("green") }} /> przygotowano plik .xls
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendTone} style={{ background: getMarkerColor("blue") }} /> uwagi do działu finansowego
          </span>
        </div>
      </div>

      {/* ---------------- Panel filtrów ---------------- */}
      <FiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onChange={updateFilters}
        onClear={clearAllFilters}
        options={options}
        total={total}
        statusOptions={config.statusOptions}
        locationLabel={config.locationLabel}
      />

      {/* ---------------- Menu kolumn ---------------- */}
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        {config.columns.map((column) => (
          <MenuItem key={column.id} dense onClick={() => toggleColumn(column.id)}>
            <Checkbox
              size="small"
              edge="start"
              checked={!hiddenColumns.includes(column.id)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={column.label} />
          </MenuItem>
        ))}
        <MenuItem dense onClick={() => setHiddenColumns([])}>
          <ListItemText primary="Przywróć domyślne" primaryTypographyProps={{ color: "primary" }} />
        </MenuItem>
      </Menu>

      {/* ---------------- Menu eksportu ---------------- */}
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem onClick={handleExportXLSX}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Excel – wszystkie wyniki"
            secondary={`${total} ${config.unitLabel} spełniających filtry`}
          />
        </MenuItem>
        <MenuItem onClick={handleFakturaReport} disabled={rows.length === 0}>
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Zestawienie fakturowe"
            secondary="tylko bieżąca strona"
          />
        </MenuItem>
      </Menu>

      {/* ---------------- Komentarz zbiorczy ---------------- */}
      <Dialog open={bulkOpen} onClose={() => !bulkSaving && setBulkOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Dodaj komentarz do zaznaczonych odpraw</DialogTitle>
        <DialogContent>
          <p style={{ margin: "0 0 12px", color: COLORS.textMuted, fontSize: 14 }}>
            Komentarz zostanie zapisany w {selectedRows.length} odprawach, a osoby
            przygotowujące i wysyłające dostaną powiadomienie.
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 14 }}>
            <strong>Numery ref:</strong> {selectedRows.map((r) => r.nrRef).join(", ")}
          </p>
          <TextField
            multiline
            fullWidth
            minRows={4}
            maxRows={8}
            autoFocus
            label="Treść komentarza"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkSaving}>
            Anuluj
          </Button>
          <Button
            onClick={handleSaveBulkComment}
            variant="contained"
            disabled={bulkSaving || !bulkText.trim()}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Potwierdzenie usunięcia ---------------- */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Usunąć zgłoszenie?</DialogTitle>
        <DialogContent>
          Zgłoszenie <strong>{deleteTarget?.nrRef}</strong> zostanie trwale usunięte.
          Tej operacji nie można cofnąć.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default UserDashboard;
